/** @format */

import {
	addProductReviewsMultimedia,
	AppProductReviewState,
	ProductReviewed,
	Review,
	ReviewReturn,
	ReviewService,
	ReviewSummary,
	selectProductIncludedInformation,
	selectProductReviewSummary,
	setBVPixelIsAvailable,
	setProductReviewState,
	setProductStatistics,
	Statistics
} from '@product-reviews/data-access';
import {
	BVEventObject,
	BVEventType,
	BVInViewData,
	BVProduct,
	MediaSourceEnum,
	TrackViewUCGObject
} from '../models';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
	afterNextRender,
	Component,
	ElementRef,
	inject,
	Input,
	input,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	Renderer2,
	signal
} from '@angular/core';
import {
	EventBusService,
	Events,
	IntersectionObservingService
} from '@shared/data-access/services';
import { forkJoin, Observable, Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { ReviewAISummaryComponent } from '../review-ai-summary/review-ai-summary.component';
import { ReviewAiProsConsComponent } from '../review-ai-pros-cons/review-ai-pros-cons.component';
import { ReviewButtonComponent } from '../review-button/review-button.component';
import { ReviewContentListComponent } from '../review-content-list/review-content-list.component';
import { ReviewLatestCarrouselComponent } from '../review-latest-carousel/review-latest-carousel.component';
import { ReviewResponsiveRatingSummaryComponent } from '../review-responsive-rating-summary/review-responsive-rating-summary.component';
import { ReviewSecondaryRatingComponent } from '../review-secondary-rating/review-secondary-rating.component';
import { ReviewhighlightCarouselComponent } from '../review-carousel/review-carousel.component';
import { ReviewsSummaryComponent } from '../reviews-summary/reviews-summary.component';
import { Store } from '@ngrx/store';
import { selectKillswitch } from '@pdp/data-access/state';

@Component({
	selector: 'product-reviews-highlight-container',
	templateUrl: './review-highlight-container.component.html',
	styleUrl: './review-highlight-container.component.scss',
	imports: [
		CommonModule,
		ReviewAiProsConsComponent,
		ReviewAISummaryComponent,
		ReviewButtonComponent,
		ReviewhighlightCarouselComponent,
		ReviewContentListComponent,
		ReviewLatestCarrouselComponent,
		ReviewResponsiveRatingSummaryComponent,
		ReviewSecondaryRatingComponent,
		ReviewsSummaryComponent
	]
})
export class ReviewHighlightContainerComponent implements OnInit, OnDestroy {
	private readonly store = inject<Store<AppProductReviewState>>(Store);
	private readonly reviewService = inject(ReviewService);
	private readonly eventBusService = inject(EventBusService);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly document = inject(DOCUMENT);
	private readonly intersectionService = inject(IntersectionObservingService);
	private readonly renderer = inject(Renderer2);
	private readonly el = inject(ElementRef);

	/**
	 *
	 * @description
	 * Product Id - Bazaar voice uses this identifier to display the correct reviews
	 */
	@Input() productIdentifier: string;

	readonly ratingCount = input<number | null>(null);
	readonly ratingValue = input<number | null>(null);

	reviewSummary: ReviewSummary;
	productName?: string;
	writeAReviewLabel = 'Write A Review';
	writeAReviewType = 'button';
	isQAEnabled = false;
	private UNSUBSCRIBE$ = new Subject<void>();
	showNewReviewsQA = this.store.selectSignal(selectKillswitch('showNewReviewsQA'));
	reviewsMVPRetest = this.store.selectSignal(selectKillswitch('reviewsMVPRetest'));
	reviewsProsCons = this.store.selectSignal(selectKillswitch('reviewsProsCons'));
	isNewReviewsEnabled = this.store.selectSignal(selectKillswitch('isNewReviewsEnabled'));
	reviewsHighlightsUpdateEnabled = this.store.selectSignal(
		selectKillswitch('reviewsHighlightsUpdateEnabled')
	);
	isReviewsAISummaryEnabled = this.store.selectSignal(
		selectKillswitch('isReviewsAISummaryEnabled')
	);
	isReviewImgPlacementEnabled = this.store.selectSignal(
		selectKillswitch('isReviewImgPlacementEnabled')
	);
	isHighlyRatedReviewsEnabled = this.store.selectSignal(
		selectKillswitch('isHighlyRatedReviewsEnabled')
	);
	isUtagBazaarvoiceEnabled = this.store.selectSignal(
		selectKillswitch('isUtagBazaarvoiceEnabled')
	);
	reviewsObserver: MutationObserver;
	private subscription: Subscription;
	mediaSource = MediaSourceEnum.CAROUSEL;

	readonly isHydrated = signal(false);

	constructor() {
		afterNextRender(() => {
			this.isHydrated.set(true);
		});
	}

	ngOnInit(): void {
		if (isPlatformBrowser(this.platformId)) {
			this.setBVPixelObserver();

			this.eventBusService.on(Events.QAndAEnabled, (isEnabled) => {
				this.isQAEnabled = isEnabled;
				this.fetchReviews();
			});

			if (this.isReviewImgPlacementEnabled()) {
				this.reviewService
					.getReviewsMultimedia$(this.productIdentifier)
					.subscribe((reviewsMultimedia) => {
						this.store.dispatch(addProductReviewsMultimedia({ reviewsMultimedia }));
					});
			}
		} else {
			//[INFO] Run in SSR to load the review information.
			this.fetchReviews();
		}

		this.store
			.select(selectProductReviewSummary)
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((summary: ReviewSummary) => {
				this.reviewSummary = summary;
			});
		this.store
			.select(selectProductIncludedInformation)
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((product: ProductReviewed) => {
				this.productName = product.name;
			});
	}

	ngOnDestroy() {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	fetchReviews() {
		const requestObservables: Observable<ReviewReturn | Review[] | Statistics>[] =
			this.isNewReviewsEnabled()
				? [
						this.reviewService.getReviews$(this.productIdentifier, {
							sortBy: 'SubmissionTime:desc',
							limitTo: 5
						})
					]
				: [
						this.reviewService.getReviews$(this.productIdentifier),
						this.reviewService.getReviewsMultimedia$(this.productIdentifier)
					];

		if (this.isQAEnabled && this.showNewReviewsQA()) {
			requestObservables.push(this.reviewService.getStatistics$(this.productIdentifier));
		}
		forkJoin(requestObservables)
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((results: [ReviewReturn, Review[], Statistics]) => {
				const [reviews] = results;
				let statistics,
					reviewsMultimedia = undefined;
				if (this.isNewReviewsEnabled()) {
					statistics = { ...results.slice(1)[0] };
				} else {
					[reviewsMultimedia, statistics] = results.slice(1);
				}
				this.store.dispatch(
					setProductReviewState({
						productIdentifier: this.productIdentifier,
						reviewSummary: reviews.reviewSummary || this.reviewSummary,
						reviews: reviews.reviews,
						product: reviews.product
					})
				);
				if (!this.isNewReviewsEnabled() && !!reviewsMultimedia) {
					this.store.dispatch(addProductReviewsMultimedia({ reviewsMultimedia }));
				}

				if (this.isQAEnabled && this.showNewReviewsQA() && !!statistics) {
					this.store.dispatch(setProductStatistics({ statistics }));
				}
			});
	}

	setBVPixelObserver(): void {
		this.subscription = this.observeDomChanges().subscribe((mutation) => {
			this.addListenertohandleBVPixel(mutation);
		});
	}

	private observeDomChanges(): Observable<MutationRecord[]> {
		return new Observable((observer) => {
			const callback = (mutationsList: MutationRecord[]) => {
				observer.next(mutationsList);
			};

			const observerInstance = new MutationObserver(callback);
			const config = { childList: true, subtree: true };

			observerInstance.observe(this.document.body, config);

			this.reviewsObserver = observerInstance;
			// Cleanup function
			return () => observerInstance.disconnect();
		});
	}

	handleBVPixel(): void {
		if (isPlatformBrowser(this.platformId)) {
			this.store
				.select(selectProductReviewSummary)
				.pipe(take(1))
				.subscribe((review: ReviewSummary) => {
					const bvInViewData: BVInViewData = {
						brand: review.brand,
						bvProduct: BVProduct.RATINGSANDREVIEWS,
						productId: this.productIdentifier
					};
					const bvObject: BVEventObject = {
						...bvInViewData,
						type: BVEventType.PRODUCT,
						categoryId: review.category,
						numReviews: review.reviewCount,
						avgRating: review.averageRating,
						percentRecommended: review.recommendationPercentage
					};

					BV.pixel.trackPageView(bvObject);

					const element = this.renderer.selectRootElement(this.el.nativeElement, true);
					if (element) {
						this.intersectionService.initIntersectionObserver(element, () => {
							this.handleBVInViewEvents(bvInViewData);
							this.intersectionService.disconnectObserver();
						});
					}
				});
		}
	}

	handleBVInViewEvents(bvInViewData: BVInViewData): void {
		const trackViewUCGData: TrackViewUCGObject = {
			minPixels: 100,
			containerId: 'product-reviews-highlight-container'
		};
		BV.pixel.trackInView(bvInViewData, trackViewUCGData);
		BV.pixel.trackViewedCGC(bvInViewData, {
			...trackViewUCGData,
			minTime: 5000
		});
	}

	addListenertohandleBVPixel(mutations: MutationRecord[]): void {
		if (
			isPlatformBrowser(this.platformId) &&
			mutations.length > 0 &&
			!!window['BV'] &&
			BV?.pixel
		) {
			this.store.dispatch(setBVPixelIsAvailable({ isBVPixelReady: true }));
			this.handleBVPixel();
			this.reviewsObserver.disconnect();
		}
	}
}
